export const CREATOR_DEFAULT_MAX_TOKENS = 1600;
export const CREATOR_LONG_FORM_MAX_TOKENS = 2000;

const STATUS_MARKER = '---STATUS---';
const EXPLANATION_MARKER = '---EXPLANATION---';
const DOCUMENT_MARKER = '---DOCUMENT---';

export function getPromptCrewExecutionFit({ prompt = {}, executeMode = 'chat' } = {}) {
  const mode = executeMode === 'creator' ? 'creator' : 'chat';
  const capabilityType = String(prompt?.capabilityType || 'prompt').toLowerCase();
  const whereToUse = String(prompt?.whereToUse || '').toLowerCase();

  if (capabilityType && capabilityType !== 'prompt') {
    return {
      canRun: false,
      mode,
      reason: 'external_capability_type',
      capabilityType,
    };
  }

  if (whereToUse === 'tools') {
    return {
      canRun: false,
      mode,
      reason: 'external_tool_platform',
      capabilityType: capabilityType || 'prompt',
    };
  }

  return {
    canRun: true,
    mode,
    reason: 'promptcrew_supported',
    capabilityType: capabilityType || 'prompt',
  };
}

function tokenizeForOverlap(value) {
  const stopWords = new Set([
    'the', 'and', 'for', 'that', 'with', 'this', 'you', 'are', 'from', 'your',
    'will', 'use', 'using', 'into', 'when', 'then', 'only', 'should', 'have',
  ]);
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word.length > 4 && !stopWords.has(word));
}

export function looksLikeRestatedCapabilityResponse(responseText, prompt = {}) {
  const response = String(responseText || '').trim();
  if (!response) return false;
  if (response.includes(DOCUMENT_MARKER)) return false;

  const lower = response.toLowerCase();
  const capabilityType = String(prompt?.capabilityType || '').toLowerCase();
  const hasInstructionHeadings = [
    'role',
    'goal',
    'instructions',
    'output requirements',
    'constraints',
    'recommended workflow',
  ].filter((heading) => new RegExp(`(^|\\n)#{0,3}\\s*${heading}\\b`, 'i').test(response)).length;
  const instructionLanguage = /\byou are\b|\byour task\b|\bfollow these instructions\b|\bcompletion criteria\b/i.test(response);

  if ((capabilityType === 'skill' || capabilityType === 'agent') && hasInstructionHeadings >= 2 && instructionLanguage) {
    return true;
  }

  const source = String(prompt?.content || prompt?.prompt || '').trim();
  if (source.length < 120 || response.length < 120) return false;

  const responseTokens = new Set(tokenizeForOverlap(response));
  const sourceTokens = tokenizeForOverlap(source);
  if (sourceTokens.length < 18 || responseTokens.size < 18) return false;

  const matches = sourceTokens.filter((token) => responseTokens.has(token)).length;
  const overlap = matches / Math.max(1, Math.min(sourceTokens.length, responseTokens.size));

  return overlap >= 0.58 && (hasInstructionHeadings >= 2 || lower.includes('capability') || instructionLanguage);
}

export function parseCreatorStatus(contentString) {
  if (!contentString || typeof contentString !== 'string') return null;
  const match = contentString.match(new RegExp(`${STATUS_MARKER}\\s*(complete|continue)\\b`, 'i'));
  return match ? match[1].toLowerCase() : null;
}

function stripStatusBlock(text) {
  if (!text || typeof text !== 'string') return '';
  const idx = text.search(new RegExp(STATUS_MARKER, 'i'));
  return (idx >= 0 ? text.slice(0, idx) : text).trim();
}

export function parseCreatorDocumentResponse(contentString) {
  if (!contentString || typeof contentString !== 'string') return null;
  const docIdx = contentString.indexOf(DOCUMENT_MARKER);
  if (docIdx === -1) return null;

  const afterDoc = contentString.slice(docIdx + DOCUMENT_MARKER.length).trimStart();
  const expIdx = afterDoc.indexOf(EXPLANATION_MARKER);
  const document = expIdx === -1
    ? stripStatusBlock(afterDoc)
    : afterDoc.slice(0, expIdx).trim();
  const explanation = expIdx === -1
    ? ''
    : stripStatusBlock(afterDoc.slice(expIdx + EXPLANATION_MARKER.length));

  return {
    document,
    explanation,
    status: parseCreatorStatus(contentString),
  };
}

export function isCreatorLongFormPrompt(promptText) {
  if (!promptText || !promptText.trim()) return false;
  const lower = promptText.toLowerCase();

  const strongSignals = [
    'whitepaper',
    'white paper',
    'multi-page',
    'multi page',
    '10-page',
    '10 page',
    '10+ page',
    'long-form',
    'long form',
    'full report',
    'report',
    'full proposal',
    'proposal',
    'comprehensive report',
    'comprehensive proposal',
    'detailed report',
    'detailed proposal',
    'full document',
    'long document',
    'manual',
    'playbook',
    'policy document',
    'resume',
    'cv',
    'cover letter',
    'professional profile',
    'executive profile',
    'bio',
    'biography',
    'case study',
    'project brief',
    'guide',
    'portfolio',
  ];

  if (strongSignals.some((kw) => lower.includes(kw))) {
    return true;
  }

  const hasDocWord = /\b(report|proposal|whitepaper|manual|guide|documentation|policy|playbook|brief|profile|bio|resume|cv)\b/.test(lower);
  const hasDetailWord = /\b(detailed|comprehensive|in-depth|full|long|complete|professional)\b/.test(lower);

  return hasDocWord && hasDetailWord;
}

export function getCreatorMaxTokens(promptText) {
  return isCreatorLongFormPrompt(promptText)
    ? CREATOR_LONG_FORM_MAX_TOKENS
    : CREATOR_DEFAULT_MAX_TOKENS;
}

export function getCreatorContinuationDecision({
  isCreator,
  isQuestion,
  status,
  finishReason,
  hasDocument,
  hasExplanation,
  contentLength,
  continueRound,
  maxContinueRounds,
}) {
  if (!isCreator || isQuestion) return { shouldContinue: false, reason: null };
  if (continueRound >= maxContinueRounds) return { shouldContinue: false, reason: 'max_rounds' };
  if (status === 'complete') return { shouldContinue: false, reason: 'status_complete' };
  if (status === 'continue') return { shouldContinue: true, reason: 'status_continue' };

  const tokenLimited = finishReason === 'length' || finishReason === 'max_output_tokens';
  if (tokenLimited) return { shouldContinue: true, reason: 'finish_length' };

  const missingCompletionSignal = hasDocument && !status;
  if (missingCompletionSignal) return { shouldContinue: true, reason: 'missing_status' };

  const looksTruncated = Boolean(contentLength > 200 && !hasExplanation);
  if (looksTruncated) return { shouldContinue: true, reason: 'missing_explanation' };

  return { shouldContinue: false, reason: 'no_continue_signal' };
}

export function summarizeCreatorDiagnostics(diagnostics) {
  if (!diagnostics || typeof diagnostics !== 'object') return '';
  const parts = [];
  if (diagnostics.continuationReason === 'status_continue') {
    parts.push('continued because the artifact was not complete');
  } else if (diagnostics.continuationReason === 'finish_length') {
    parts.push('continued because the model hit the output limit');
  } else if (diagnostics.continuationReason === 'missing_status') {
    parts.push('continued because completion status was missing');
  } else if (diagnostics.continuationReason === 'missing_explanation') {
    parts.push('continued because the response looked incomplete');
  } else if (diagnostics.continuationReason === 'max_rounds') {
    parts.push('stopped after the continuation cap');
  }

  if (diagnostics.round > 0) parts.push(`round ${diagnostics.round + 1}`);
  if (diagnostics.maxTokens) parts.push(`${diagnostics.maxTokens} token cap`);
  return parts.join('; ');
}
